/**
 * Pinned from `/tmp/integrate` (branch `integrate/wave-a`, read-only source):
 *   - src/modules/risk/risk-events.controller.ts + .service.ts
 *   - src/modules/risk/risk-health.controller.ts + .service.ts
 *   - src/modules/risk/risk-rules.controller.ts + .service.ts
 *   - src/modules/risk/risk-reviewers.controller.ts + .service.ts
 *   - src/modules/risk/dto/*.ts
 *   - src/shared/domain/risk-rules.ts (RiskRuleSettings, AfterHoursParams)
 * One file, so a later change on that side is a one-file fix here.
 *
 * ADR-0017 in three sentences that shape every screen in this module: no
 * score, no model — every finding is a rule, a window, a count/amount and the
 * threshold it crossed, in one sentence. Nothing here acts; it only produces
 * cases for a NAMED reviewer roster to work. `risk.review` is the one key
 * that exposes a subject's identity, and it is granted narrowly on purpose.
 */
import { useCallback, useEffect, useRef } from 'react';
import { RiskEventStatus, RiskRuleKey, RiskSeverity } from '@/lib/shared-types';

export { RiskEventStatus, RiskRuleKey, RiskSeverity, RESOLVABLE_RISK_EVENT_STATUSES } from '@/lib/shared-types';

// --------------------------------------------------------------- errors

export function apiError(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: string | string[] } } })?.response
    ?.data?.message;
  if (Array.isArray(message)) return message.filter(Boolean).join(' · ');
  return (typeof message === 'string' && message.trim()) || fallback;
}

export function apiStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status;
}

// ------------------------------------------------------------ case queue

/** `GET /risk/events` row and `GET /risk/events/:id`'s own top-level fields — the raw `RiskEvent` row, no included user names. */
export interface RiskEvent {
  id: string;
  tenantId: string;
  branchId: string | null;
  rule: RiskRuleKey;
  severity: RiskSeverity;
  status: RiskEventStatus;
  /** Null for the two rules that measure a branch rather than a person. Never invented. */
  subjectUserId: string | null;
  windowStart: string;
  windowEnd: string;
  observedCount: number;
  observedAmount: string;
  thresholdCount: number | null;
  thresholdAmount: string | null;
  /** The whole finding as one sentence — rendered at detection time, never regenerated. Show this, never a score. */
  explanation: string;
  dedupeKey: string;
  assignedReviewerId: string | null;
  resolutionNote: string | null;
  resolvedById: string | null;
  resolvedAt: string | null;
  lastObservedAt: string;
  createdAt: string;
  updatedAt: string;
}

/** One audited fact behind a finding, linked back to the row it was built from. */
export interface RiskEventEvidence {
  id: string;
  riskEventId: string;
  entityType: string;
  entityId: string;
  auditLogId: string | null;
  actorId: string | null;
  occurredAt: string;
  amount: string | null;
  description: string;
  createdAt: string;
}

export interface RiskEventNote {
  id: string;
  riskEventId: string;
  authorId: string | null;
  body: string;
  createdAt: string;
}

/** `GET /risk/events/:id` — the case, its evidence and its notes. */
export interface RiskEventDetail extends RiskEvent {
  evidence: RiskEventEvidence[];
  notes: RiskEventNote[];
}

export interface QueryRiskEventsParams {
  page?: number;
  limit?: number;
  status?: RiskEventStatus;
  severity?: RiskSeverity;
  rule?: RiskRuleKey;
  branchId?: string;
  subjectUserId?: string;
  assignedReviewerId?: string;
}

export interface ResolveRiskEventInput {
  status: RiskEventStatus;
  /** Required for every terminal outcome; optional only for UNDER_REVIEW. */
  note?: string;
}

// ----------------------------------------------------------------- health

/** `GET /risk/health` — verbatim `RiskHealth`. Silence is the expected healthy state; this is what tells it apart from broken. */
export interface RiskHealth {
  healthy: boolean;
  enabledRules: number;
  totalRules: number;
  reviewers: number;
  openEvents: number;
  lastSweepStartedAt: string | null;
  lastSuccessfulSweepAt: string | null;
  /** Zero on a venue with rules enabled, after a clean run, is the unbound-tenant-read signature — not a quiet venue. */
  factsScannedLastSweep: number | null;
  problems: string[];
}

// ------------------------------------------------------------------ rules

export interface AfterHoursParams {
  startHour: number;
  endHour: number;
}

/** The shape shared by a configured row and an unconfigured rule's would-be defaults. */
export interface RiskRuleSettings {
  rule: RiskRuleKey;
  enabled: boolean;
  windowMinutes: number;
  countThreshold: number | null;
  amountThreshold: string | null;
  severity: RiskSeverity;
  params: AfterHoursParams | null;
}

/** `GET /risk/rules` row — every `RiskRuleKey`, configured or not, defaults shown but not applied. */
export interface RiskRuleListItem extends RiskRuleSettings {
  configured: boolean;
  shippedDefault: Omit<RiskRuleSettings, 'rule'>;
}

export interface UpdateRiskRuleInput {
  enabled?: boolean;
  windowMinutes?: number;
  /** `null` explicitly disables the count arm; `undefined` leaves it as it is. */
  countThreshold?: number | null;
  /** `null` explicitly disables the amount arm; a decimal string never a float. */
  amountThreshold?: string | null;
  severity?: RiskSeverity;
  /** AFTER_HOURS_ACTIVITY only. */
  startHour?: number;
  endHour?: number;
}

// -------------------------------------------------------------- reviewers

/** `GET /risk/reviewers` row. The whole notified audience — not derived from a permission. */
export interface RiskReviewer {
  id: string;
  tenantId: string;
  userId: string;
  minSeverity: RiskSeverity;
  createdAt: string;
}

export interface UpsertRiskReviewerInput {
  userId: string;
  minSeverity?: RiskSeverity;
}

// ------------------------------------------------------------ presentation

/**
 * Local, and deliberately not routed through `@/components/ui/badge`'s shared
 * `toneForStatus`: that table already maps the strings `OPEN`, `CONFIRMED` and
 * `DISMISSED` to tones chosen for OTHER domains (a session opening, a merge
 * completing). `CONFIRMED` there is `success` — green — because a completed
 * merge is a good outcome; a CONFIRMED risk finding is the opposite of a good
 * outcome, and painting it the same colour a successful transaction gets would
 * be exactly the kind of thing this module's own care about presentation is
 * for. So risk statuses get their own mapping, not a shared string table.
 */
export function riskStatusTone(
  status: RiskEventStatus,
): 'neutral' | 'success' | 'warning' | 'danger' | 'info' {
  switch (status) {
    case RiskEventStatus.OPEN:
      return 'warning';
    case RiskEventStatus.UNDER_REVIEW:
      return 'info';
    case RiskEventStatus.CONFIRMED:
      return 'danger';
    case RiskEventStatus.FALSE_POSITIVE:
      return 'neutral';
    case RiskEventStatus.DISMISSED:
      return 'neutral';
    default:
      return 'neutral';
  }
}

export function riskSeverityTone(
  severity: RiskSeverity,
): 'neutral' | 'success' | 'warning' | 'danger' | 'info' {
  switch (severity) {
    case RiskSeverity.HIGH:
      return 'danger';
    case RiskSeverity.MEDIUM:
      return 'warning';
    case RiskSeverity.LOW:
      return 'neutral';
    default:
      return 'neutral';
  }
}

// ----------------------------------------------------------------- polling

/**
 * Why the whole module polls instead of subscribing: `src/events/event-names.ts`
 * carries no `RISK_*` entry at all — `RiskSweepService` and `RiskEventsService`
 * emit nothing, because ADR-0017 is explicit that "nothing here acts" and the
 * sweep is a scheduled batch job (`@Cron`, every 10 minutes), not a
 * request/response flow with a moment to emit from. There is therefore no
 * event this module failed to wire up; polling is the only freshness model
 * that exists on the backend side of this feature.
 *
 * Same shape as `Pages/Reports/lib.ts`'s copy (that file's own comment notes
 * every module with no realtime events keeps its own copy of this rather than
 * sharing one): stops while the tab is hidden, skips a tick while the
 * previous reload is still in flight.
 */
export function useAutoRefresh(
  reload: () => void,
  { enabled, intervalMs }: { enabled: boolean; intervalMs: number },
) {
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  const inFlight = useRef(false);

  const tick = useCallback(() => {
    if (inFlight.current) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    inFlight.current = true;
    try {
      reloadRef.current();
    } finally {
      window.setTimeout(() => {
        inFlight.current = false;
      }, 0);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(tick, intervalMs);
    return () => window.clearInterval(id);
  }, [enabled, intervalMs, tick]);
}
